import { type ReactNode, useCallback, useMemo } from 'react';
import { type NavigateOptions, useLocation, useNavigate } from 'react-router-dom';
import {
  BookOutlined,
  CloseCircleOutlined,
  PieChartOutlined,
  ShareAltOutlined,
  SolutionOutlined,
} from '@ant-design/icons';

import BeaconLogo from '@/components/Beacon/BeaconLogo';

import { CATALOGUE_SEARCH_STORAGE_KEY } from '@/features/catalogue/useCatalogueUrlSync';
import { FORCE_CATALOGUE } from '@/config';
import { useMetadata, useScopeHasData, useSelectedScope } from '@/features/metadata/hooks';
import { type DiscoveryScope, selectScope } from '@/features/metadata/metadata.store';
import type { MenuItem } from '@/types/navigation';
import { BentoRoute } from '@/types/routes';
import { useAppDispatch, useLanguage, useTranslationFn } from '@/hooks';
import { getCurrentPage, langAndScopeSelectionToUrl, scopeToUrl } from '@/utils/router';

/** Prefixes a path with the currently-selected i18n language. */
export const useLangPrefixedUrl = (path: string): string => {
  const language = useLanguage();
  if (path.length > 1) {
    path = path.replace('^/', ''); // strip slash prefix if present
  }
  return `/${language}/${path}`;
};

export const useNavigateToRoot = () => {
  const navigate = useNavigate();
  const rootUrl = useLangPrefixedUrl('');
  return useCallback(() => navigate(rootUrl), [navigate, rootUrl]);
};

/**
 * Like useNavigateToRoot, but for "back to catalogue" links: restores the catalogue's last query
 * string (facet filters, search, sort, view) from sessionStorage, since this is a fresh navigation
 * rather than a browser-history pop and would otherwise land on the catalogue with no filters applied.
 *
 * Dispatches the blank scope synchronously before navigating, same as useNavigateToScope: otherwise
 * ScopedRoute's URL/scope reconciliation effect briefly sees the *old* (dataset/project) scope next to
 * the new blank-scope URL, decides they mismatch, and replaces the URL back to the old scoped path -
 * clobbering the restored query string.
 */
export const useNavigateToCatalogue = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const rootUrl = useLangPrefixedUrl('');
  return useCallback(() => {
    dispatch(selectScope({}));
    const search = sessionStorage.getItem(CATALOGUE_SEARCH_STORAGE_KEY) ?? '';
    navigate(rootUrl + search);
  }, [dispatch, navigate, rootUrl]);
};

/**
 * The purpose of useNavigateToScope is to provide a `navigate(...)`-like hook which goes to a possibly-new scope, and
 * sets the Redux scope at the same time as setting the URL to prevent weird state-update / URL-update race conditions
 * with downstream search processing.
 */
export const useNavigateToScope = () => {
  const language = useLanguage();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  return useCallback(
    (
      newScope: DiscoveryScope,
      suffix: string = '',
      fixedProjectAndDataset: boolean = false,
      navigateOptions: NavigateOptions | undefined = undefined
    ) => {
      // This action will internally handle already-equal scope selections to avoid accidental re-renders:
      dispatch(selectScope(newScope));
      navigate(scopeToUrl(newScope, language, suffix, fixedProjectAndDataset), navigateOptions);
    },
    [dispatch, navigate, language]
  );
};

/**
 * Hook which returns a URL suffix prefixed by the current language and selected scope.
 */
export const useCurrentScopePrefixedUrl = (suffix: string) => {
  const language = useLanguage();
  const selectedScope = useSelectedScope();
  return langAndScopeSelectionToUrl(language, selectedScope, suffix);
};

/**
 * The purpose of useNavigateToSameScopeUrl is to provide a `navigate(...)`-like hook which goes to page within the same
 * scope as the one currently in Redux.
 */
export const useNavigateToSameScopeUrl = () => {
  const language = useLanguage();
  const navigate = useNavigate();
  const selectedScope = useSelectedScope();

  return useCallback(
    (suffix: string, replace: boolean = true) => {
      navigate(langAndScopeSelectionToUrl(language, selectedScope, suffix), { replace });
    },
    [language, navigate, selectedScope]
  );
};

export const useIsInCatalogueMode = () => {
  const { projects } = useMetadata();
  return projects.length !== 1 || FORCE_CATALOGUE;
};

export const useGetRouteTitleAndIcon = () => {
  const location = useLocation();
  const catalogueMode = useIsInCatalogueMode();

  // Use location for catalogue page detection instead of selectedProject, since it gives us faster UI rendering at the
  // cost of only being wrong with a redirect edge case (and being slightly more brittle).
  const overviewIsCatalogue = !location.pathname.includes('/p/') && !location.pathname.includes('/d/') && catalogueMode;

  return useCallback(
    (routeId: string): [string, ReactNode] => {
      /* eslint-disable react/jsx-key */
      switch (routeId) {
        case BentoRoute.Overview:
          return overviewIsCatalogue ? ['Catalogue', <BookOutlined />] : ['Overview', <PieChartOutlined />];
        case BentoRoute.Provenance:
          return ['Provenance', <SolutionOutlined />];
        case BentoRoute.Beacon:
          return ['Beacon', <BeaconLogo />];
        case BentoRoute.BeaconNetwork:
          return ['Beacon Network', <ShareAltOutlined />];
        case BentoRoute.Phenopackets:
          return ['entities.phenopacket_other', <SolutionOutlined />];
        case BentoRoute.NotFound: // should not be used, but not unknown
          return ['errors.page_not_found', <CloseCircleOutlined />];
        default:
          console.error('Unknown page', routeId);
          return ['', null];
      }
      /* eslint-enable react/jsx-key */
    },
    [overviewIsCatalogue]
  );
};

/**
 * Hook returning a 'tuple' of [site header menu items, scope header menu items]. Menu item arrays with only one entry
 * should not be displayed by the UI.
 */
export const useSiteMenuItems = (): [MenuItem[], MenuItem[]] => {
  const t = useTranslationFn();
  const { fixedProject, fixedDataset, scope } = useSelectedScope();
  const location = useLocation();
  const page = getCurrentPage(location);

  const scopeHasData = useScopeHasData();

  const createMenuItem = useCallback(
    (key: string, label: string, icon?: ReactNode, children?: MenuItem[]): MenuItem => ({
      key,
      icon,
      children,
      label: t(label),
    }),
    [t]
  );

  const getRouteTitleAndIcon = useGetRouteTitleAndIcon();

  return useMemo(() => {
    // Serves weird overloaded purpose as both catalogue and data overview route:
    const overviewItem = createMenuItem(BentoRoute.Overview, ...getRouteTitleAndIcon(BentoRoute.Overview));

    const topBarItems: MenuItem[] = [overviewItem];
    const scopeItems: MenuItem[] = [overviewItem];

    const putInTopBar = fixedDataset || (fixedProject && !scope.dataset) || !scope.project;

    if (page !== BentoRoute.Phenopackets && (page !== BentoRoute.BeaconNetwork || putInTopBar)) {
      const itemsRef = putInTopBar ? topBarItems : scopeItems;

      if (BentoRoute.Beacon && scopeHasData) {
        itemsRef.push(createMenuItem(BentoRoute.Beacon, ...getRouteTitleAndIcon(BentoRoute.Beacon)));
      }

      // TODO: can enable for project if we get a more extensive project model
      if (scope.dataset) {
        itemsRef.push(createMenuItem(BentoRoute.Provenance, ...getRouteTitleAndIcon(BentoRoute.Provenance)));
      }
    }

    if (BentoRoute.BeaconNetwork && putInTopBar) {
      topBarItems.push(createMenuItem(BentoRoute.BeaconNetwork, ...getRouteTitleAndIcon(BentoRoute.BeaconNetwork)));
    }

    return (scopeItems.length > 1 ? [[], scopeItems] : [topBarItems, []]) as [MenuItem[], MenuItem[]];
  }, [getRouteTitleAndIcon, createMenuItem, scope, fixedProject, fixedDataset, scopeHasData, page]);
};
